import React from 'react';
import { useGetAssociateActiveActionPlansQuery } from '../../coachingActionSlice';
import { AssociateActiveActionPlan } from '../associateCAPTabs-types';
import { metricValueFormat } from '../../../../utils/metricValueFormat';

// ── Helpers ──────────────────────────────────────────────────────────────────
const SESSION_ORDINALS = ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth'];

const getSessionLabel = (sessionNumber: number): string => {
    if (sessionNumber === 0) return 'Coaching Started';
    const ordinal = SESSION_ORDINALS[sessionNumber - 1] ?? `${sessionNumber}th`;
    return `${ordinal} Follow Up`;
};

const getDaySuffix = (day: number): string => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
    }
};

const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const day = date.getDate();
    const suffix = getDaySuffix(day);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric', day: 'numeric' })
        .replace(/\d+,/, `${day}${suffix},`);
};

// ── Component ─────────────────────────────────────────────────────────────────
interface Props {
    empNo: string;
    setShowAcknowledge: React.Dispatch<React.SetStateAction<boolean>>;
    setActionPlanId: React.Dispatch<React.SetStateAction<number>>;
    setSessionId: React.Dispatch<React.SetStateAction<number>>;
    setVia: React.Dispatch<React.SetStateAction<string>>;
}

const AssociateCAPActionPlans: React.FC<Props> = ({ empNo, setShowAcknowledge, setActionPlanId, setSessionId, setVia }) => {
    const { data, isLoading, isError } = useGetAssociateActiveActionPlansQuery(empNo, {
        skip: !empNo,
    });

    const plans: AssociateActiveActionPlan[] = data?.data ?? [];

    if (isLoading || !data) {
        return <p className="cap-tabs__empty">Loading…</p>;
    }

    if (isError) {
        return (
            <p className="cap-tabs__empty cap-tabs__empty--error">
                <i className="pi pi-exclamation-triangle" style={{ marginRight: '0.4rem' }} />
                Unable to load active action plans. Please try again later.
            </p>
        );
    }

    if (plans.length === 0) {
        return <p className="cap-tabs__empty">No active action plans.</p>;
    }
    const onAcknowledgeClick = (plan: AssociateActiveActionPlan) => {
        setActionPlanId(plan.out_coaching_action_plan_id);
        setSessionId(plan.out_coaching_action_plan_session_id);
        setShowAcknowledge(true);
        setVia('active');
    };
    return (
        <div className="cap-tabs__grid">
            {plans.map((plan) => (
                <div
                    key={`${plan.out_coaching_action_plan_id}-${plan.out_coaching_action_plan_session_id}`}
                    className="cap-tabs__row"
                >
                    {/* Metric */}
                    <div className="cap-tabs__cell cap-tabs__cell--metric">
                        <span className="cap-tabs__label">Metric</span>
                        <span className="cap-tabs__metric-name">{plan.out_label}</span>
                    </div>
                    <div className="cap-tabs__divider" />

                    {/* Started / Next Step */}
                    <div className="cap-tabs__cell cap-tabs__cell--dates">
                        <span className="cap-tabs__date-line">
                            Started on: {formatDate(plan.out_coaching_start_date)}
                        </span>
                        <span className="cap-tabs__date-line cap-tabs__next-step">
                            Next step: 
                            <strong>{plan.out_follow_up_date
                                ? ` Follow Up on ${formatDate(plan.out_follow_up_date)}`
                                : ' Follow Up — not scheduled'}</strong>
                        </span>
                    </div>

                    <div className="cap-tabs__divider" />

                    {/* Performance Commitment */}
                    <div className="cap-tabs__cell cap-tabs__cell--stat">
                        <span className="cap-tabs__label">Performance Commitment</span>
                        <span className="cap-tabs__stat-value">
                            {plan.out_performance_commitment != null
                                ? metricValueFormat(plan.out_performance_commitment, plan.out_metric_type_name)
                                : '—'}
                        </span>
                    </div>

                    <div className="cap-tabs__divider" />

                    {/* Current Metric Score */}
                    <div className="cap-tabs__cell cap-tabs__cell--stat">
                        <span className="cap-tabs__label">Current Metric Score</span>
                        <span className="cap-tabs__stat-value">
                            {plan.out_metric_current_value != null
                                ? metricValueFormat(plan.out_metric_current_value, plan.out_metric_type_name)
                                : '—'}
                        </span>
                    </div>

                    <div className="cap-tabs__divider" />

                    {/* Session */}
                    <div className="cap-tabs__cell cap-tabs__cell--session">
                        <span className="cap-tabs__label">Session</span>
                        <span className={`cap-tabs__pill ${plan.out_session_number === 0 ? 'cap-tabs__pill--coaching' : 'cap-tabs__pill--followup'}`}>
                            {getSessionLabel(plan.out_session_number)}
                        </span>
                    </div>

                    <div className="cap-tabs__divider" />

                    {/* Acknowledged */}
                    <div className="cap-tabs__cell cap-tabs__cell--action">
                        <span className="cap-tabs__label">Acknowledged</span>
                        <span className={`cap-tabs__pill ${plan.out_acknowledged ? 'cap-tabs__pill--recognized' : 'cap-tabs__pill--insufficient'}`}>
                            {plan.out_acknowledged ? 'Yes' : 'No'}
                        </span>
                    </div>

                    {/* Action button */}
                    <div className="cap-tabs__cell cap-tabs__cell--btn">
                        <button
                            className={`cap-tabs__btn ${plan.out_acknowledged ? 'cap-tabs__btn--view' : 'cap-tabs__btn--acknowledge'}`}
                            onClick={() => onAcknowledgeClick(plan)}
                        >
                            {plan.out_acknowledged ? 'View' : 'Acknowledge'}
                            <i className="pi pi-arrow-circle-right cap-tabs__btn-icon" />
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default AssociateCAPActionPlans;